import EventEmitter from "eventemitter3";
import AppConfig from "../config/app";

const TIME_BETWEEN_SAVES = 10000; //10 seconds

/**
 * @typedef {import("@/managers/types.mjs").DesignData} DesignData
 * @typedef {import("@/managers/DesignFile").default} DesignFile
 */

/**
 * Coordinates local file state and saves with remote files.
 * Controls autosave timer and file references.
 * Save flow looks like updateFile -> (autosave or immediate) -> trigger save on 
 * all files unless saveNow is used which will stop timer and save files immediately.
 */
class DesignManager extends EventEmitter {
  /** @type {Record<string, DesignFile>} */
  files;

  constructor() {
    super();

    this.autoSaveProcessId = null;
    this.inSync = true;

    this.savingEnabled = true;
    this.inError = false;

    this.files = {};
  }

  /**
   * Adds file to manager and adds callbacks to save state events
   * @param {DesignFile} designFile
   */
  addFile(designFile) {
    this.files[designFile.id] = designFile;
    designFile.on("save", this._fileSaveSuccess.bind(this));
    designFile.on("error", this._fileSaveError.bind(this));
  }

  /**
   * Gets a design file
   * @param {string} fileId
   * @returns {DesignFile}
   */
  getFile(fileId) {
    return this.files[fileId];
  }

  /**
   * Returns true if manager contains file by id
   * @param {string} fileId
   * @returns {boolean}
   */
  hasFile(fileId) {
    return fileId in this.files;
  }

  /**
   * Marks file usage state
   * @param {string} fileId
   * @param {boolean} inUse
   */
  setFileInUse(fileId, inUse) {
    const file = this.getFile(fileId);
    if (file) {
      file.inUse = inUse;
    }
  }

  /**
   * Removes design file from manager.
   * @param {string} fileId
   * @returns {void}
   */
  removeFile(fileId) {
    if (AppConfig.demoMode) {
      return;
    }

    const file = this.getFile(fileId);
    if (file) {
      file.destroy();
      file.removeAllListeners();
      // todo: is this line necessary? Deleting should remove reference without setting null
      this.files[fileId] = null;
      delete this.files[fileId];
    }
  }

  /**
   * Updates file data.
   * @param {DesignData} fileData
   * @param {boolean} forceInSync - Update file without starting a save operation
   */
  updateFile(fileData, forceInSync = false) {
    const id = fileData.get("id");
    const file = this.getFile(id);
    if (file) {
      file.update(fileData);
      if (!file.inSync && !forceInSync) {
        this._updateInSync(false);
        this._autoSaveInit();
      }
    }
  }

  /**
   * Save design. Unused as far as I can tell.
   */
  save() {
    if (!AppConfig.demoMode) {
      this._updateInSync(false);
      this._autoSaveInit();
    }
  }

  /**
   * force saving of files stop autosave
   */
  saveNow() {
    this._autoSaveStop();
    this._checkForUpdates();
  }

  /**
   * Checks if all files are in sync and updates sync state
   */
  _checkFiles() {
    const synced = Object.values(this.files).every((file) => file.inSync);
    this._updateInSync(synced);
  }

  /**
   * Checks files for sync state, 
   */
  _checkForUpdates() {
    let needsUpdate = false;

    Object.values(this.files).forEach((file) => {
      if (!file.inSync) {
        needsUpdate = true;
        file.save();
      }

      if (file.canRemove()) {
        this.removeFile(file.id);
      }
    });

    if (!needsUpdate) {
      this._updateInSync(true);
      this._autoSaveStop();
    } else {
      this._updateInSync(false);
    }
  }

  /**
   * Updates manager sync state and emits the state change
   * @param {boolean} inSync 
   */
  _updateInSync(inSync) {
    this.inSync = inSync;
    this.emit("statusChange", this.inSync);
  }

  /**
   * Starts autosave timer if not running and saving is allowed
   */
  _autoSaveInit() {
    if (!AppConfig.demoMode && this.autoSaveProcessId == null) {
      console.log("Starting Auto Save");
      this.autoSaveProcessId = setInterval(
        this._checkForUpdates.bind(this),
        TIME_BETWEEN_SAVES,
      );
    }
  }

  /**
   * Clears autosave timer
   */
  _autoSaveStop() {
    if (this.autoSaveProcessId) {
      clearInterval(this.autoSaveProcessId);
      this.autoSaveProcessId = null;
    }
  }

  /**
   * Called when a file is successfully changed and checks all files in an attempt to settle sync state.
   * @todo could this be collapsed into _checkForUpdates? Seems like an extra hop for the same functionality.
   * @param {DesignFile} file 
   */
  _fileSaveSuccess(file) {
    this._checkFiles();
    console.log(file.id, "Saved", "In Sync", this.inSync);
  }

  /**
   * Called when file doesn't save successfully.
   * @param {*} error 
   * @param {DesignFile} file 
   * @returns 
   */
  _fileSaveError(error, file) {
    console.log(file.id, "Error");
    if (file.isUnableToSave()) {
      // Don't show error modal for pages that were added to site json but failed to save
      if (
        file._isCleanupCandidate &&
        file._cleanupStore &&
        file._cleanupPageId
      ) {
        if (file._cleanupStore.pagesPendingSave.has(file._cleanupPageId)) {
          console.log(
            "Triggering site json cleanup for page:",
            file._cleanupPageId,
          );
        }
        // todo: does the file need to be destroyed here too?
        return;
      }

      this._autoSaveStop();
      this.inError = true;
      this.emit("error", error);
    }
  }

  /**
   * Returns design sync state
   * @returns {boolean}
   */
  needsToSave() {
    return !this.inSync;
  }

  /**
   * Returns true if autosave timer is running
   * @returns {boolean}
   */
  isAutoSaveEnabled() {
    return this.autoSaveProcessId != null;
  }

  /** 
   * Clears store files and resets
   */
  reset() {
    this._autoSaveStop();

    Object.values(this.files).forEach((file) => {
        file.destroy();
        // todo: should this just be called internally to the DesignFile on destroy?
        file.removeAllListeners();
        this.files[file.id] = null;
    })

    this.files = {};
    this.inSync = true;
  }
}

export default new DesignManager();
